import type { NormalizedPoint } from "./types";

const SHAPE_VIEWBOX_SIZE = 1_000;
const MAX_POLYGON_POINTS = 128;
const MIN_POLYGON_AREA = 0.000_001;
const SMOOTH_CORNER_DEGREES = 138;
const CORNER_INSET_RATIO = 0.5;
const MIN_CONNECTOR_LENGTH = 0.000_1;

interface RegionShapeViewport {
  width: number;
  height: number;
}

interface NormalizedRegionBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const regionShapeViewBox = `0 0 ${SHAPE_VIEWBOX_SIZE} ${SHAPE_VIEWBOX_SIZE}`;

function validPolygon(polygon: NormalizedPoint[] | undefined): NormalizedPoint[] | null {
  if (!Array.isArray(polygon) || polygon.length < 3 || polygon.length > MAX_POLYGON_POINTS) return null;
  if (polygon.some((point) => (
    !Number.isFinite(point?.x)
    || !Number.isFinite(point?.y)
    || point.x < 0
    || point.x > 1
    || point.y < 0
    || point.y > 1
  ))) return null;
  const twiceArea = polygon.reduce((sum, point, index) => {
    const next = polygon[(index + 1) % polygon.length];
    return sum + point.x * next.y - next.x * point.y;
  }, 0);
  return Math.abs(twiceArea) / 2 >= MIN_POLYGON_AREA ? polygon : null;
}

function percentage(value: number): string {
  return `${Number((value * 100).toFixed(5))}%`;
}

export function normalizedPolygonClipPath(
  polygon: NormalizedPoint[] | undefined,
  bounds: NormalizedRegionBounds
): string | null {
  const points = validPolygon(polygon);
  if (!points || !Number.isFinite(bounds.x) || !Number.isFinite(bounds.y)
    || !Number.isFinite(bounds.width) || !Number.isFinite(bounds.height)
    || bounds.width <= 0 || bounds.height <= 0) return null;

  const epsilon = 0.001;
  const relative = points.map((point) => ({
    x: (point.x - bounds.x) / bounds.width,
    y: (point.y - bounds.y) / bounds.height
  }));
  if (relative.some((point) => (
    point.x < -epsilon || point.x > 1 + epsilon || point.y < -epsilon || point.y > 1 + epsilon
  ))) return null;

  return `polygon(${relative.map((point) => (
    `${percentage(Math.min(1, Math.max(0, point.x)))} ${percentage(Math.min(1, Math.max(0, point.y)))}`
  )).join(", ")})`;
}

export function normalizedPolygonPath(
  polygon: NormalizedPoint[] | undefined,
  viewport?: RegionShapeViewport
): string | null {
  const valid = validPolygon(polygon);
  if (!valid) return null;

  const points = valid.map((point) => ({
    x: point.x * SHAPE_VIEWBOX_SIZE,
    y: point.y * SHAPE_VIEWBOX_SIZE
  }));
  const classificationWidth = viewport && Number.isFinite(viewport.width) && viewport.width > 0
    ? viewport.width
    : SHAPE_VIEWBOX_SIZE;
  const classificationHeight = viewport && Number.isFinite(viewport.height) && viewport.height > 0
    ? viewport.height
    : SHAPE_VIEWBOX_SIZE;
  const classificationPoints = valid.map((point) => ({
    x: point.x * classificationWidth,
    y: point.y * classificationHeight
  }));
  const smooth = classificationPoints.map((point, index) => {
    const previous = classificationPoints[(index - 1 + classificationPoints.length) % classificationPoints.length];
    const next = classificationPoints[(index + 1) % classificationPoints.length];
    const previousLength = Math.hypot(point.x - previous.x, point.y - previous.y);
    const nextLength = Math.hypot(next.x - point.x, next.y - point.y);
    const denominator = previousLength * nextLength;
    const cosine = denominator === 0 ? -1 : (
      (previous.x - point.x) * (next.x - point.x) +
      (previous.y - point.y) * (next.y - point.y)
    ) / denominator;
    const angle = Math.acos(Math.max(-1, Math.min(1, cosine))) * 180 / Math.PI;
    return angle >= SMOOTH_CORNER_DEGREES;
  });
  const entry = points.map((point, index) => {
    if (!smooth[index]) return point;
    const previous = points[(index - 1 + points.length) % points.length];
    return {
      x: point.x + (previous.x - point.x) * CORNER_INSET_RATIO,
      y: point.y + (previous.y - point.y) * CORNER_INSET_RATIO
    };
  });
  const exit = points.map((point, index) => {
    if (!smooth[index]) return point;
    const next = points[(index + 1) % points.length];
    return {
      x: point.x + (next.x - point.x) * CORNER_INSET_RATIO,
      y: point.y + (next.y - point.y) * CORNER_INSET_RATIO
    };
  });
  const commands = [`M${entry[0].x} ${entry[0].y}`];
  let current = entry[0];
  const connectTo = (point: { x: number; y: number }) => {
    if (Math.hypot(point.x - current.x, point.y - current.y) <= MIN_CONNECTOR_LENGTH) return;
    commands.push(`L${point.x} ${point.y}`);
    current = point;
  };
  for (let index = 0; index < points.length; index += 1) {
    if (index > 0) connectTo(entry[index]);
    if (smooth[index]) {
      commands.push(`Q${points[index].x} ${points[index].y} ${exit[index].x} ${exit[index].y}`);
      current = exit[index];
    }
  }
  connectTo(entry[0]);
  return `${commands.join(" ")} Z`;
}
